import { useState } from 'react';
import { formatDateOnly } from '../../../../../utils/dates';
import type { OrdersBrowse } from '../useOrdersBrowse';
import { ChipDateField } from './ChipDateField';
import { ChipOptionList } from './ChipOptionList';
import {
    SCANNED_OPTIONS,
    scannedChipValue,
    seedCustomRange,
    type ScannedPreset
} from './chipPresets';
import { FilterBar } from './FilterBar';
import { FilterChip } from './FilterChip';

interface OrderFilterBarProps {
    browse: OrdersBrowse;
    today: string;
}

interface CustomRangeFieldsProps extends OrderFilterBarProps {
    autoFocus: boolean;
    onDone: () => void;
}

function CustomRangeFields({ browse, today, autoFocus, onDone }: CustomRangeFieldsProps) {
    const { customFrom, customTo } = browse.filters;
    const invalid = browse.rangeError !== null;
    return (
        <div className="flex flex-col gap-1.5 border-t border-gray-100 px-2.5 pb-1.5 pt-2.5">
            <div className="flex gap-2">
                <ChipDateField
                    label="From"
                    value={customFrom}
                    autoFocus={autoFocus}
                    max={customTo || today}
                    invalid={invalid}
                    onChange={(value) => browse.updateFilters({ customFrom: value })}
                    onDone={onDone}
                />
                <ChipDateField
                    label="To"
                    value={customTo}
                    min={customFrom || undefined}
                    max={today}
                    invalid={invalid}
                    onChange={(value) => browse.updateFilters({ customTo: value })}
                    onDone={onDone}
                />
            </div>
            {browse.rangeError && (
                <p role="alert" className="text-[12px] text-red-600">
                    {browse.rangeError}
                </p>
            )}
        </div>
    );
}

function ScannedChip({ browse, today }: OrderFilterBarProps) {
    const { scanned, customFrom, customTo } = browse.filters;
    const custom = { from: customFrom, to: customTo };
    const [focusRange, setFocusRange] = useState(false);

    const select = (next: ScannedPreset) => {
        if (next !== 'custom') {
            browse.updateFilters({ scanned: next });
            return;
        }
        const seeded = seedCustomRange(scanned, custom, today);
        browse.updateFilters({ scanned: next, customFrom: seeded.from, customTo: seeded.to });
    };

    return (
        <FilterChip label="Scanned" value={scannedChipValue(scanned, custom, formatDateOnly)}>
            {(close) => (
                <div className="w-[300px]">
                    <ChipOptionList<ScannedPreset>
                        options={SCANNED_OPTIONS}
                        selected={scanned}
                        onSelect={(next) => {
                            select(next);
                            setFocusRange(next === 'custom');
                            if (next !== 'custom') close('choice');
                        }}
                    />
                    {scanned === 'custom' && (
                        <CustomRangeFields
                            browse={browse}
                            today={today}
                            autoFocus={focusRange}
                            onDone={() => close('choice')}
                        />
                    )}
                </div>
            )}
        </FilterChip>
    );
}

export function OrderFilterBar({ browse, today }: OrderFilterBarProps) {
    return (
        <FilterBar
            label="Order filters"
            chips={<ScannedChip browse={browse} today={today} />}
            error={browse.rangeError}
            isUpdating={browse.isUpdating}
            showClear={browse.showClear}
            onClear={browse.clearFilters}
        />
    );
}
